import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { Typography } from '@mui/material';
// import type { iconData } from '../../types/cards';
import type { Icons } from '../../types/icons';
import { StyledCrossIconButton } from '../component/ThemeComponent';
import { addIcon, updateIcon } from '../../services/api/OttIcon';

const useStyles = makeStyles({
	inputField: {
		marginBottom: '15px',
	},
	imagePreview: {
		width: '100%',
		height: '200px',
		marginTop: '10px',
		objectFit: 'contain',
	},
});

const VisuallyHiddenInput = styled('input')({
	clip: 'rect(0 0 0 0)',
	clipPath: 'inset(50%)',
	height: 1,
	overflow: 'hidden',
	position: 'absolute',
	bottom: 0,
	left: 0,
	whiteSpace: 'nowrap',
	width: 1,
});

interface CreateCategoriesProps {
	open: boolean;
	handleClose: () => void;
	fetchCategories: () => void;
	iconData?: Icons | null;
}

const CreateIcon: React.FC<CreateCategoriesProps> = ({
	open,
	handleClose,
	fetchCategories,
	iconData,
}) => {
	const classes = useStyles();
	const [name, setName] = useState('');
	const [image, setImage] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [nameError, setNameError] = useState('');
	const [imageError, setImageError] = useState('');
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (iconData) {
			setName(iconData.name);
			setImagePreview(typeof iconData?.imageUrl === 'string' ? iconData.imageUrl : null);
		} else {
			setName('');
			setImage(null);
			setImagePreview(null);
		}
		// Reset errors when the dialog is opened or iconData changes
		setNameError('');

		setImageError('');
	}, [iconData, open]);

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (
			file &&
			file.size <= 1024 * 1024 * 5 &&
			['image/jpeg', 'image/png'].includes(file.type)
		) {
			setImage(file);
			setImagePreview(URL.createObjectURL(file));
			setImageError('');
		} else {
			alert('Image must be JPEG or PNG and less than 5MB');
			setImageError('Image is required');
		}
	};

	const validateForm = () => {
		let valid = true;
		if (!name) {
			setNameError('Name is required');
			valid = false;
		} else {
			setNameError('');
		}

		if (!image && !iconData?.imageUrl) {
			setImageError('Image is required');
			valid = false;
		}
		return valid;
	};

	// const handleSubmit = async (event: React.FormEvent) => {
	// 	event.preventDefault();
	// 	if (validateForm()) {
	// 		const formDataToSend = new FormData();
	// 		formDataToSend.append('name', name);
	// 		if (image) {
	// 			formDataToSend.append('ottappImage', image);
	// 		}

	// 		try {
	// 			setLoading(true);
	// 			if (iconData) {
	// 				const response = await updateIcon(formDataToSend, iconData._id.toString());
	// 				if (response.success) {
	// 					// toast.success('iconData updated successfully');
	// 					toast.success(response.message);
	// 				} else {
	// 					toast.error('Failed to update icons');
	// 				}
	// 			} else {
	// 				const newiconData = await addIcon(formDataToSend);
	// 				if (newiconData.success) {
	// 					toast.success(newiconData.message);
	// 				} else {
	// 					toast.error(`${newiconData.data.errors}`);
	// 				}
	// 			}
	// 			handleClose();
	// 			fetchCategories();
	// 		} catch (error) {
	// 			toast.error('Failed to save iconData');
	// 		} finally {
	// 			setLoading(false);
	// 		}
	// 	}
	// };

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (validateForm()) {
			const formDataToSend = new FormData();
			formDataToSend.append('name', name);
			if (image) {
				formDataToSend.append('ottappImage', image);
			}

			try {
				setLoading(true);
				if (iconData) {
					// Ensure the iconData._id is correctly passed
					const response = await updateIcon(formDataToSend, iconData._id.toString());
					if (response.success) {
						toast.success(response.message);
					} else {
						toast.error(response?.data?.message);
						console.log(response?.data?.message, 'response.message');
						console.log(response?.data?.message, 'response');
					}
				} else {
					const newIconData = await addIcon(formDataToSend);
					if (newIconData.success) {
						toast.success(newIconData.message);
					} else {
						toast.error(`${newIconData?.data?.message}`);
					}
				}
				handleClose();
				fetchCategories();
			} catch (error) {
				toast.error('Failed to save icon');
			} finally {
				setLoading(false);
			}
		}
	};

	const handleCancel = () => {
		setName('');
		setImage(null);
		setImagePreview(null);
		setNameError('');
		setImageError('');
		handleClose();
	};

	return (
		<>
			{/* <ToastContainer autoClose={3000} /> */}
			<Dialog open={open} onClose={handleCancel}>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						borderBottom: '1px solid #c9c9c9',
					}}
				>
					<span>{iconData ? 'Edit Icon' : 'Add Icon'}</span>
					<StyledCrossIconButton onClick={handleCancel}>
						<CloseIcon />
					</StyledCrossIconButton>
				</DialogTitle>
				<DialogContent>
					<TextField
						margin="dense"
						label="Icon Name"
						fullWidth
						value={name}
						onChange={(e) => setName(e.target.value)}
						className={classes.inputField}
						size="small"
						error={!!nameError}
						helperText={nameError}
						required
						style={{ marginTop: '15px' }}
					/>
					<Typography sx={{ margin: '0.5rem 0px' }}>
						<Button
							component="label"
							role={undefined}
							variant="contained"
							tabIndex={-1}
							startIcon={<ImageIcon />}
						>
							Upload Image file
							<VisuallyHiddenInput
								type="file"
								onChange={handleImageChange}
								accept="image/jpeg,image/png"
							/>
						</Button>
						<br />
						{imageError && <div style={{ color: 'red' }}>{imageError}</div>}
						{imagePreview ? (
							<img
								src={imagePreview}
								className={classes.imagePreview}
								alt="iconData Preview"
								style={{ width: '250px', height: '200px', marginTop: '5px' }}
							/>
						) : (
							<span
								style={{ color: '#8f8e8e', paddingLeft: '5px', fontSize: '14px' }}
							>
								{/* <br /> */}
								Upload the image up to 5MB Size and should be JPEG, JPG or PNG
							</span>
						)}
					</Typography>
				</DialogContent>
				<DialogActions style={{ borderTop: '1px solid #c9c9c9' }}>
					<Button onClick={handleCancel}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CreateIcon;
